import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/cn";
import { listProviders, type ProviderConfig } from "@/api/providers";
import { useClientPagination } from "@/hooks/usePagination";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useSyncCursorPage } from "@/hooks/useSyncCursorPage";
import { PaginationBar } from "@/components/pagination-bar";
import { canSelectForScan } from "@/components/providers/provider-detail-sections";
import {
  useDiscoveryProfile,
  useSaveDiscoveryProfile,
  enabledProviderCount,
} from "@/hooks/useDiscovery";
import {
  CollectionCommandBar,
  DataTable,
  EntityCard,
  EntityCompactItem,
  EntityListLayout,
  useClientTableSort,
  useListViewMode,
  type Column,
  type EntityFilterSelectField,
  type EntitySortOption,
  type BadgeConfig,
} from "@/components/collection";
import {
  TYPE_LABELS,
  TYPE_COLORS,
  TYPE_FILTER_TABS,
  PORTAL_SELECTION_FILTER_TABS,
  filterPortalProviders,
  type ProviderTypeFilter,
  type PortalSelectionFilter,
  isScannableBoardProvider,
} from "@/lib/provider-ui";

const ALLOWED_MODES = ["cards", "list", "table"] as const;

const SORT_OPTIONS: EntitySortOption[] = [
  { field: "name", order: "asc", label: "Name A–Z" },
  { field: "type", order: "asc", label: "Type" },
];

function applySort(
  items: ProviderConfig[],
  sort: EntitySortOption,
): ProviderConfig[] {
  const dir = sort.order === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    if (sort.field === "type") {
      return a.type.localeCompare(b.type) * dir;
    }
    return a.name.localeCompare(b.name) * dir;
  });
}

function selectionLabel(tab: PortalSelectionFilter): string {
  if (tab === "selected") return "In my scan";
  if (tab === "unselected") return "Not in scan";
  return "All";
}

function statusBadges(
  provider: ProviderConfig,
  isSelected: boolean,
  selectable: boolean,
): BadgeConfig[] {
  const badges: BadgeConfig[] = [];
  if (isSelected && selectable) {
    badges.push({
      label: "In your scan",
      colorClass: "bg-brand/10 text-brand",
    });
  }
  if (!provider.enabled) {
    badges.push({
      label: "Disabled globally",
      colorClass: "bg-muted text-muted-foreground",
    });
  }
  if (provider.type !== "board") {
    badges.push({
      label: "View only",
      colorClass: "bg-muted text-muted-foreground",
    });
  }
  return badges;
}

export function PortalsProvidersPanel() {
  const navigate = useNavigate();
  const { data: profile } = useDiscoveryProfile();
  const saveMutation = useSaveDiscoveryProfile();
  const [filterTab, setFilterTab] = useState<ProviderTypeFilter>("board");
  const [selectionFilter, setSelectionFilter] =
    useState<PortalSelectionFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState<EntitySortOption>(SORT_OPTIONS[0]);
  const [message, setMessage] = useState<string | null>(null);

  const { viewMode, setViewMode, allowedModes } = useListViewMode({
    storageKey: "viewMode_portals_providers",
    defaultMode: "cards",
    allowedModes: [...ALLOWED_MODES],
  });

  const jobBoards = profile?.jobBoards;
  const selectedBoards = useMemo(
    () => new Set(jobBoards ?? []),
    [jobBoards],
  );
  const selectedCount = enabledProviderCount(profile);

  // Cursor when browsing; load-all when search or selection filter needs full set.
  const useServerCursor =
    !searchQuery.trim() &&
    selectionFilter === "all" &&
    (filterTab === "all" || filterTab === "board");

  const cursorPag = useCursorPagination([filterTab]);

  const {
    data: cursorData,
    isLoading: cursorLoading,
    error: cursorError,
  } = useQuery({
    queryKey: [
      "providers",
      "portals-cursor",
      cursorPag.pageSize,
      cursorPag.cursor,
    ],
    queryFn: () =>
      listProviders({
        limit: cursorPag.pageSize,
        cursor: cursorPag.cursor,
      }),
    enabled: useServerCursor,
    staleTime: 30_000,
  });

  useSyncCursorPage(
    cursorPag.setNextCursor,
    cursorData,
    useServerCursor && !cursorLoading && !!cursorData,
  );

  const {
    data: allProviders = [],
    isLoading: allLoading,
    error: allError,
  } = useQuery({
    queryKey: ["providers", "portals-all"],
    queryFn: async () => (await listProviders()).providers,
    enabled: !useServerCursor,
    staleTime: 30_000,
  });

  const isLoading = useServerCursor ? cursorLoading : allLoading;
  const error = useServerCursor ? cursorError : allError;
  const providers = useServerCursor
    ? (cursorData?.providers ?? [])
    : allProviders;

  const filteredProviders = useMemo(
    () =>
      applySort(
        filterPortalProviders(providers, selectedBoards, {
          search: useServerCursor ? "" : searchQuery,
          type: filterTab,
          selection: useServerCursor ? "all" : selectionFilter,
        }).filter((p) => p.type !== "board" || isScannableBoardProvider(p)),
        sortOption,
      ),
    [
      providers,
      selectedBoards,
      searchQuery,
      filterTab,
      selectionFilter,
      sortOption,
      useServerCursor,
    ],
  );

  const clientPag = useClientPagination(
    useServerCursor ? [] : filteredProviders,
    [
      filterTab,
      searchQuery,
      selectionFilter,
      sortOption.field,
      sortOption.order,
    ],
  );

  const pageItems = useServerCursor
    ? filteredProviders
    : clientPag.pageItems;
  const getSortValue = useCallback((p: ProviderConfig, key: string) => {
    if (key === "type") return p.type;
    return p.name;
  }, []);

  const { sortedItems, sortKey, sortDirection, onSortChange } =
    useClientTableSort({
      items: pageItems.map((p) => ({ ...p, id: p.providerId })),
      defaultKey: null,
      getValue: getSortValue,
    });

  async function toggleBoard(providerId: string) {
    setMessage(null);
    const current = profile?.jobBoards ?? [];
    const next = selectedBoards.has(providerId)
      ? current.filter((id) => id !== providerId)
      : [...current, providerId];

    try {
      await saveMutation.mutateAsync({ jobBoards: next });
      setMessage("Saved your board selections.");
    } catch (err) {
      setMessage(
        err instanceof Error ? err.message : "Failed to save board selection.",
      );
    }
  }

  const filterFields: EntityFilterSelectField[] = [
    {
      id: "type",
      label: "Type",
      value: filterTab === "all" ? "" : filterTab,
      allLabel: "All types",
      options: TYPE_FILTER_TABS.filter((t) => t !== "all").map((t) => ({
        value: t,
        label: TYPE_LABELS[t],
      })),
      onChange: (v) => setFilterTab((v || "all") as ProviderTypeFilter),
    },
    {
      id: "selection",
      label: "Selection",
      value: selectionFilter === "all" ? "" : selectionFilter,
      allLabel: "All",
      options: PORTAL_SELECTION_FILTER_TABS.filter((t) => t !== "all").map(
        (t) => ({
          value: t,
          label: selectionLabel(t),
        }),
      ),
      onChange: (v) =>
        setSelectionFilter((v || "all") as PortalSelectionFilter),
    },
  ];

  const scanToggle = (provider: ProviderConfig) => {
    const isSelected = selectedBoards.has(provider.providerId);
    const selectable = canSelectForScan(provider);
    const globallyDisabled = !provider.enabled;

    if (!selectable) {
      return (
        <span className="text-xs text-muted-foreground">
          {globallyDisabled
            ? "Enable in catalog"
            : provider.type !== "board"
              ? "Not scannable here"
              : "Unavailable"}
        </span>
      );
    }

    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void toggleBoard(provider.providerId);
        }}
        disabled={saveMutation.isPending}
        className={cn(
          "flex items-center gap-2 rounded-md px-2 py-1 text-xs font-medium transition",
          isSelected
            ? "bg-brand/10 text-brand"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        <span
          className={cn(
            "flex h-5 w-5 items-center justify-center rounded border text-[10px] font-bold",
            isSelected
              ? "border-brand bg-brand text-brand-foreground"
              : "border-border",
          )}
        >
          {isSelected ? "✓" : ""}
        </span>
        {isSelected ? "In scan" : "Add to scan"}
      </button>
    );
  };

  const manageLink = (providerId: string) => (
    <Link
      to={`/settings/discovery/providers/${providerId}`}
      className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground transition hover:text-foreground"
      onClick={(e) => e.stopPropagation()}
    >
      Manage
    </Link>
  );

  const columns: Column<ProviderConfig & { id: string }>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (p) => (
        <span className="font-medium text-foreground">{p.name}</span>
      ),
    },
    {
      key: "type",
      header: "Type",
      sortable: true,
      render: (p) => (
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-[10px] font-medium",
            TYPE_COLORS[p.type],
          )}
        >
          {TYPE_LABELS[p.type]}
        </span>
      ),
    },
    {
      key: "scan",
      header: "Scan",
      render: (p) => scanToggle(p),
    },
    {
      key: "actions",
      header: "",
      className: "text-right",
      render: (p) => manageLink(p.providerId),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-muted-foreground">
          Choose which board aggregators to include in Find Jobs → Boards. Enable
          employers from an employer profile or Find Jobs → My sources. Global catalog
          settings live in{" "}
          <Link to="/settings/providers" className="text-brand hover:underline">
            Settings → Providers
          </Link>
          .
        </p>
      </div>

      {!isLoading && !error && (useServerCursor || providers.length > 0) && (
        <div>
          <CollectionCommandBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search by name, description, or ID…"
            sortOption={sortOption}
            onSortChange={setSortOption}
            sortOptions={SORT_OPTIONS}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            allowedModes={allowedModes}
            filterFields={filterFields}
            activeFilterCount={
              (filterTab !== "all" ? 1 : 0) +
              (selectionFilter !== "all" ? 1 : 0)
            }
            onClear={() => {
              setSearchQuery("");
              setFilterTab("all");
              setSelectionFilter("all");
            }}
          />
          {!useServerCursor && (
            <p className="mt-2 text-xs text-muted-foreground">
              {filteredProviders.length === 0
                ? `0 of ${providers.length} (${selectedCount} in your scan)`
                : `Showing ${clientPag.showingFrom}–${clientPag.showingTo} of ${filteredProviders.length} (${selectedCount} in your scan)`}
            </p>
          )}
        </div>
      )}
      {message && <p className="text-xs text-muted-foreground">{message}</p>}

      {isLoading && (
        <div className="flex items-center justify-center rounded-lg border border-border bg-card p-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}

      {error && !isLoading && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load providers.
        </div>
      )}

      {!isLoading && !error && providers.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No providers configured.
        </div>
      )}

      {!isLoading &&
        !error &&
        !useServerCursor &&
        providers.length > 0 &&
        filteredProviders.length === 0 && (
          <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            No providers match your filters.
          </div>
        )}

      {!isLoading &&
        !error &&
        useServerCursor &&
        pageItems.length === 0 &&
        providers.length > 0 && (
          <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            No providers on this page match the current type filter.
          </div>
        )}

      {!isLoading &&
        !error &&
        (pageItems.length > 0 ||
          (useServerCursor && (providers.length > 0 || cursorPag.page > 1))) && (
        <>
          {pageItems.length > 0 && (
          <EntityListLayout
            viewMode={viewMode}
            cards={
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {pageItems.map((provider) => {
                  const isSelected = selectedBoards.has(provider.providerId);
                  const selectable = canSelectForScan(provider);
                  const globallyDisabled = !provider.enabled;
                  const badges = statusBadges(
                    provider,
                    isSelected,
                    selectable,
                  );

                  return (
                    <EntityCard
                      key={provider.providerId}
                      id={provider.providerId}
                      title={provider.name}
                      description={
                        provider.description || provider.providerId
                      }
                      primaryBadge={{
                        label: TYPE_LABELS[provider.type],
                        colorClass: TYPE_COLORS[provider.type],
                      }}
                      metadata={
                        badges.length > 0
                          ? badges.map((b) => ({
                              value: (
                                <span
                                  className={cn(
                                    "rounded-full px-2 py-0.5 text-[10px]",
                                    b.colorClass,
                                  )}
                                >
                                  {b.label}
                                </span>
                              ),
                            }))
                          : undefined
                      }
                      className={cn(
                        isSelected && selectable && "border-brand/40",
                        globallyDisabled && "opacity-60 bg-card/50",
                      )}
                      footerContent={
                        <div className="flex items-center justify-between">
                          {scanToggle(provider)}
                          {manageLink(provider.providerId)}
                        </div>
                      }
                      onClick={() =>
                        navigate(`/settings/discovery/providers/${provider.providerId}`)
                      }
                    />
                  );
                })}
              </div>
            }
            list={
              <div className="space-y-2">
                {pageItems.map((provider) => {
                  const isSelected = selectedBoards.has(provider.providerId);
                  const selectable = canSelectForScan(provider);
                  const globallyDisabled = !provider.enabled;
                  const badges: BadgeConfig[] = [
                    {
                      label: TYPE_LABELS[provider.type],
                      colorClass: TYPE_COLORS[provider.type],
                    },
                    ...statusBadges(provider, isSelected, selectable),
                  ];

                  return (
                    <EntityCompactItem
                      key={provider.providerId}
                      id={provider.providerId}
                      title={provider.name}
                      description={
                        provider.description || provider.providerId
                      }
                      badges={badges}
                      className={cn(
                        isSelected && selectable && "border-brand/40",
                        globallyDisabled && "opacity-60 bg-card/50",
                      )}
                      actions={
                        <>
                          {scanToggle(provider)}
                          {manageLink(provider.providerId)}
                        </>
                      }
                      onClick={() =>
                        navigate(`/settings/discovery/providers/${provider.providerId}`)
                      }
                    />
                  );
                })}
              </div>
            }
            table={
              <DataTable
                data={sortedItems}
                columns={columns}
                onRowClick={(p) =>
                  navigate(`/settings/discovery/providers/${p.providerId}`)
                }
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
                emptyMessage="No providers match your filters."
              />
            }
          />
          )}
          {useServerCursor ? (
            <PaginationBar
              mode="cursor"
              page={cursorPag.page}
              pageSize={cursorPag.pageSize}
              itemCount={providers.length}
              showingFrom={
                providers.length === 0 ? 0 : cursorPag.showingFrom
              }
              showingTo={cursorPag.showingTo(providers.length)}
              hasMore={cursorPag.hasMore}
              canPrev={cursorPag.canPrev}
              onFirst={cursorPag.goFirst}
              onPrev={cursorPag.goPrev}
              onNext={cursorPag.goNext}
              onPageSizeChange={cursorPag.setPageSize}
            />
          ) : (
            <PaginationBar
              page={clientPag.page}
              pageSize={clientPag.pageSize}
              totalItems={filteredProviders.length}
              totalPages={clientPag.totalPages}
              showingFrom={clientPag.showingFrom}
              showingTo={clientPag.showingTo}
              onPageChange={clientPag.setPage}
              onPageSizeChange={clientPag.setPageSize}
            />
          )}
        </>
      )}
    </div>
  );
}
